import { Check, Lock, type LucideIcon } from 'lucide-react';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import { createContext, use, useId, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';

import { cn } from './cn';

// Card de escolha do Fluxo guiado (U53, discovery/13 §3.1): ícone, título e descrição sempre à vista, desabilitado
// com o motivo à vista e o selecionado com wash + linha + ✓ (a cor sozinha não basta). Escolha única sobre o Radix
// RadioGroup (role=radio, setas); múltipla com role=checkbox.

/** Uma opção do grupo de cards. */
export interface ChoiceOption {
  readonly value: string;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly icon?: LucideIcon;
  /** `true` desliga; um texto desliga e diz por quê, à vista (W05). */
  readonly disabled?: boolean | string | undefined;
}

type Commit = (value: string) => void;

const ChoiceCommitContext = createContext<Commit | undefined>(undefined);

/**
 * Quem ouve a escolha explícita (clique, Enter, Espaço; as setas só movem a seleção) dos cards abaixo. O
 * `FlowScreen` usa no passo com `autoAdvance` (W07).
 */
export function ChoiceCommitProvider({ onCommit, children }: { readonly onCommit: Commit | undefined; readonly children: ReactNode }) {
  return <ChoiceCommitContext value={onCommit}>{children}</ChoiceCommitContext>;
}

interface GroupState {
  readonly multiple: boolean;
  readonly selected: readonly string[];
  readonly toggle: (value: string) => void;
  readonly select: (value: string) => void;
  readonly commit: Commit;
  readonly disabled: boolean;
}

const GroupContext = createContext<GroupState | null>(null);

const CARD = cn(
  'group/choice relative grid min-h-16 w-full grid-cols-[2.5rem_minmax(0,1fr)] items-start gap-3 rounded-card border border-border bg-card p-3.5 pr-10 text-left text-foreground shadow-1',
  'transition-[border-color,background-color,translate] duration-(--lume-dur-fast) ease-(--lume-ease-out)',
  'enabled:hover:-translate-y-px enabled:hover:border-input motion-reduce:enabled:hover:translate-y-0',
  // O Radix marca o rádio com `aria-checked` também: uma regra serve aos dois papéis.
  'aria-checked:border-accent-line aria-checked:bg-accent-wash aria-checked:ring-1 aria-checked:ring-accent-line aria-checked:ring-inset',
  'disabled:border-dashed disabled:bg-transparent disabled:shadow-none',
);


function reasonOf(disabled: ChoiceOption['disabled']): string | undefined {
  return typeof disabled === 'string' && disabled !== '' ? disabled : undefined;
}

export interface ChoiceCardProps extends ChoiceOption {
  readonly className?: string;
}

/** Um card dentro de um `ChoiceCardGroup`. */
export function ChoiceCard({ value, title, description, icon: Icon, disabled, className }: ChoiceCardProps) {
  const group = use(GroupContext);
  if (!group) throw new Error('ChoiceCard fora de um ChoiceCardGroup');
  const id = useId();
  const reason = reasonOf(disabled);
  const off = group.disabled || (disabled !== undefined && disabled !== false);
  const titleId = `${id}-title`;
  const describedBy = [description ? `${id}-desc` : null, reason ? `${id}-reason` : null].filter(Boolean).join(' ') || undefined;
  const isOn = group.selected.includes(value);

  // Escolha explícita: clique de verdade (o Radix clica por código ao andar pelas setas, `detail` 0) ou Enter/Espaço.
  const onClick = (event: MouseEvent) => {
    if (event.detail > 0) group.commit(value);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.key === 'Enter' && !group.multiple) group.select(value);
    group.commit(value);
  };

  const body = (
    <>
      <span
        aria-hidden
        className={cn(
          'inline-flex size-10 items-center justify-center rounded-lg bg-surface text-accent-text',
          'group-aria-checked/choice:bg-card',
          'group-disabled/choice:text-muted-foreground',
        )}
      >
        {Icon ? <Icon className="size-5" /> : null}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span id={titleId} className="text-base leading-snug font-semibold group-disabled/choice:text-muted-foreground">
          {title}
        </span>
        {description ? (
          // Sobre o wash o `fg-muted` não passa AA (4,25:1, lume-contrast.test): no selecionado a descrição vai em `fg`.
          <span id={`${id}-desc`} className={cn('text-sm text-muted-foreground', 'group-aria-checked/choice:text-foreground')}>
            {description}
          </span>
        ) : null}
        {reason ? (
          <span id={`${id}-reason`} className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock aria-hidden className="size-3 shrink-0" />
            {reason}
          </span>
        ) : null}
      </span>
      <span
        aria-hidden
        className={cn(
          'absolute top-3 right-3 inline-flex size-5 items-center justify-center rounded-full border border-input text-primary-foreground',
          'group-aria-checked/choice:border-accent group-aria-checked/choice:bg-accent',
          'group-disabled/choice:border-dashed',
        )}
      >
        <Check
          className={cn(
            'size-3 scale-50 opacity-0 transition-[opacity,scale] duration-(--lume-dur-fast) ease-(--lume-ease-out)',
            'group-aria-checked/choice:scale-100 group-aria-checked/choice:opacity-100',
          )}
          strokeWidth={3}
        />
      </span>
    </>
  );

  if (group.multiple)
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={isOn}
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        disabled={off}
        data-slot="choice-card"
        data-value={value}
        onClick={() => group.toggle(value)}
        className={cn(CARD, className)}
      >
        {body}
      </button>
    );
  return (
    <RadioGroupPrimitive.Item
      value={value}
      disabled={off}
      aria-labelledby={titleId}
      aria-describedby={describedBy}
      data-slot="choice-card"
      data-value={value}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={cn(CARD, className)}
    >
      {body}
    </RadioGroupPrimitive.Item>
  );
}

interface GroupBase {
  /** Atalho para os cards; ou passe `ChoiceCard` como filhos. */
  readonly options?: readonly ChoiceOption[] | undefined;
  readonly children?: ReactNode;
  readonly 'aria-label'?: string | undefined;
  readonly 'aria-labelledby'?: string | undefined;
  readonly 'aria-describedby'?: string | undefined;
  readonly 'aria-invalid'?: boolean | undefined;
  readonly 'aria-required'?: boolean | undefined;
  readonly id?: string | undefined;
  readonly name?: string | undefined;
  readonly disabled?: boolean | undefined;
  /** Escolha explícita (clique, Enter, Espaço), mesmo do card já marcado: o auto-avanço (W07) parte daqui. */
  readonly onCommit?: Commit | undefined;
  readonly className?: string | undefined;
}

export type ChoiceCardGroupProps = GroupBase &
  (
    | { readonly multiple?: false; readonly value: string | null; readonly onValueChange: (value: string) => void }
    | { readonly multiple: true; readonly value: readonly string[]; readonly onValueChange: (value: string[]) => void }
  );

const GRID = 'grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,14.5rem),1fr))]';

/** Grupo de cards de escolha: única (radiogroup, setas) ou múltipla (`multiple`, checkboxes). */
export function ChoiceCardGroup(props: ChoiceCardGroupProps) {
  const fromContext = use(ChoiceCommitContext);
  const { options, children, onCommit, className, disabled = false } = props;
  const commit: Commit = (value) => {
    onCommit?.(value);
    fromContext?.(value);
  };
  const cards = children ?? options?.map((option) => <ChoiceCard key={option.value} {...option} />);
  const aria = {
    'aria-label': props['aria-label'],
    'aria-labelledby': props['aria-labelledby'],
    'aria-describedby': props['aria-describedby'],
    'aria-invalid': props['aria-invalid'],
    'aria-required': props['aria-required'],
  };
  if (props.multiple) {
    const selected = props.value;
    const toggle = (value: string) => props.onValueChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
    return (
      <GroupContext value={{ multiple: true, selected, toggle, select: toggle, commit, disabled }}>
        <div role="group" id={props.id} data-slot="choice-card-group" className={cn(GRID, className)} {...aria}>
          {cards}
        </div>
      </GroupContext>
    );
  }
  const selected = props.value === null ? [] : [props.value];
  const select = (value: string) => {
    if (value !== props.value) props.onValueChange(value);
  };
  return (
    <GroupContext value={{ multiple: false, selected, toggle: select, select, commit, disabled }}>
      <RadioGroupPrimitive.Root
        id={props.id}
        name={props.name}
        value={props.value ?? ''}
        onValueChange={select}
        disabled={disabled}
        loop
        data-slot="choice-card-group"
        className={cn(GRID, className)}
        {...aria}
      >
        {cards}
      </RadioGroupPrimitive.Root>
    </GroupContext>
  );
}
